import { useCallback, useEffect, useState } from 'react';
import { Link as RouterLink, useLocation, useNavigate } from 'react-router-dom';
import {
  Alert,
  AppBar,
  Box,
  Button,
  Chip,
  Divider,
  Drawer,
  IconButton,
  List,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Stack,
  Toolbar,
  Typography,
  useMediaQuery,
  useTheme,
} from '@mui/material';
import MenuIcon from '@mui/icons-material/Menu';
import DashboardIcon from '@mui/icons-material/Dashboard';
import Inventory2Icon from '@mui/icons-material/Inventory2';
import PointOfSaleIcon from '@mui/icons-material/PointOfSale';
import StoreIcon from '@mui/icons-material/Store';
import SettingsIcon from '@mui/icons-material/Settings';
import LogoutIcon from '@mui/icons-material/Logout';
import StorefrontIcon from '@mui/icons-material/Storefront';
import ReceiptLongIcon from '@mui/icons-material/ReceiptLong';
import CampaignIcon from '@mui/icons-material/Campaign';
import SmartToyIcon from '@mui/icons-material/SmartToy';
import AccountBalanceWalletIcon from '@mui/icons-material/AccountBalanceWallet';
import NotificationsActiveIcon from '@mui/icons-material/NotificationsActive';
import HistoryIcon from '@mui/icons-material/History';
import PeopleIcon from '@mui/icons-material/People';
import { useAuth } from '../auth';
import api from '../api';
import { goToAuthHub } from '../authHub';
import { SUPPORT_WHATSAPP_URL, typeLabel, vendorCurrency } from '../constants';
import LpGosnelAiFab from './LpGosnelAiFab';
import LpWhatsAppFab from './LpWhatsAppFab';
import LpAiReturnChip from './LpAiReturnChip';

const DRAWER_WIDTH = 260;

const NAV = [
  { to: '/orders', label: 'Resident orders', icon: <ReceiptLongIcon /> },
  { to: '/dashboard', label: 'Dashboard', icon: <DashboardIcon /> },
  { to: '/notifications-help', label: 'Order alerts', icon: <NotificationsActiveIcon /> },
  { to: '/delivery-staff', label: 'Delivery Staff', icon: <PeopleIcon /> },
  { to: '/products', label: 'Manage items', icon: <Inventory2Icon /> },
  { to: '/ads', label: 'Promote / Ads', icon: <CampaignIcon /> },
  { to: '/topup', label: 'Top up wallet', icon: <AccountBalanceWalletIcon /> },
  { to: '/transactions', label: 'Transactions', icon: <HistoryIcon /> },
  { to: '/ai', label: 'GoSnel AI', icon: <SmartToyIcon /> },
  { to: '/sales', label: 'Sales', icon: <PointOfSaleIcon /> },
  { to: '/shop', label: 'Shop profile', icon: <StoreIcon /> },
  { to: '/settings', label: 'Settings', icon: <SettingsIcon /> },
];

export default function AppShell({ children }) {
  const { vendor, logout } = useAuth();
  const location = useLocation();
  const navigate = useNavigate();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const [mobileOpen, setMobileOpen] = useState(false);
  const [walletBalance, setWalletBalance] = useState(null);
  const [showWalletBanner, setShowWalletBanner] = useState(false);
  const [walletCurrencyApi, setWalletCurrencyApi] = useState(null);
  const [soldTotal, setSoldTotal] = useState(null);
  const currency = walletCurrencyApi || vendorCurrency(vendor);

  const loadHeaderStats = useCallback(async () => {
    try {
      const [balRes, statsRes] = await Promise.all([
        api.get('/vendor/auth/finance/balance'),
        api.get('/api/towers/shop/stats').catch(() => null),
      ]);
      const data = balRes.data || {};
      const bal = data.balance ?? data.wallet_balance;
      const n = typeof bal === 'number' ? bal : Number(bal);
      setWalletBalance(Number.isFinite(n) ? n : null);
      if (data.wallet_currency) {
        setWalletCurrencyApi(String(data.wallet_currency).toUpperCase());
      }
      if (typeof data.show_wallet_banner === 'boolean') {
        setShowWalletBanner(data.show_wallet_banner);
      } else {
        const thr = Number(data.banner_threshold);
        setShowWalletBanner(
          Number.isFinite(n) && Number.isFinite(thr) && n <= thr
        );
      }
      const sold = statsRes?.data?.sold_total;
      const sn = typeof sold === 'number' ? sold : Number(sold);
      setSoldTotal(Number.isFinite(sn) ? sn : null);
    } catch {
      setWalletBalance(null);
      setShowWalletBanner(false);
      setSoldTotal(null);
    }
  }, []);

  useEffect(() => {
    if (!vendor) return;
    loadHeaderStats();
  }, [vendor, loadHeaderStats, location.pathname]);

  const goLogout = () => {
    logout();
    goToAuthHub();
  };

  const drawer = (
    <Box sx={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
      <Box sx={{ px: 2, py: 2.25 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.25 }}>
          {vendor?.logo_url ? (
            <Box
              component="img"
              src={vendor.logo_url}
              alt=""
              sx={{
                width: 40,
                height: 40,
                borderRadius: 2,
                objectFit: 'cover',
                border: '1px solid',
                borderColor: 'divider',
                bgcolor: '#fff',
              }}
            />
          ) : (
            <Box
              sx={{
                width: 40,
                height: 40,
                borderRadius: 2,
                bgcolor: '#0f766e',
                color: '#fff',
                display: 'grid',
                placeItems: 'center',
              }}
            >
              <StorefrontIcon fontSize="small" />
            </Box>
          )}
          <Box sx={{ minWidth: 0 }}>
            <Typography fontWeight={900} noWrap>
              Local Partners
            </Typography>
            <Typography variant="caption" color="text.secondary" noWrap>
              {typeLabel(vendor?.vendor_type)}
            </Typography>
          </Box>
        </Box>
        <Typography
          variant="body2"
          fontWeight={800}
          sx={{ mt: 1.5 }}
          noWrap
          title={vendor?.name || ''}
        >
          {vendor?.name || 'Shop'}
        </Typography>
      </Box>
      <Divider />
      <List sx={{ flex: 1, px: 1, py: 1, overflowY: 'auto' }}>
        {NAV.map((item) => {
          const active =
            location.pathname === item.to || location.pathname.startsWith(`${item.to}/`);
          return (
            <ListItemButton
              key={item.to}
              component={RouterLink}
              to={item.to}
              selected={active}
              onClick={() => isMobile && setMobileOpen(false)}
              sx={{
                borderRadius: 2,
                mb: 0.5,
                '&.Mui-selected': {
                  bgcolor: 'rgba(15, 118, 110, 0.12)',
                  color: '#0f766e',
                  '& .MuiListItemIcon-root': { color: '#0f766e' },
                },
              }}
            >
              <ListItemIcon sx={{ minWidth: 40 }}>{item.icon}</ListItemIcon>
              <ListItemText
                primary={item.label}
                primaryTypographyProps={{ fontWeight: active ? 800 : 600 }}
              />
            </ListItemButton>
          );
        })}
      </List>
      <Divider />
      <List sx={{ px: 1, py: 1 }}>
        <ListItemButton onClick={goLogout} sx={{ borderRadius: 2 }}>
          <ListItemIcon sx={{ minWidth: 40 }}>
            <LogoutIcon />
          </ListItemIcon>
          <ListItemText primary="Log out" primaryTypographyProps={{ fontWeight: 700 }} />
        </ListItemButton>
      </List>
    </Box>
  );

  return (
    <Box sx={{ display: 'flex', minHeight: '100dvh', bgcolor: 'background.default', overflowX: 'hidden' }}>
      <AppBar
        position="fixed"
        elevation={0}
        sx={{
          bgcolor: '#0f766e',
          width: { md: `calc(100% - ${DRAWER_WIDTH}px)` },
          ml: { md: `${DRAWER_WIDTH}px` },
          pt: 'env(safe-area-inset-top)',
        }}
      >
        <Toolbar sx={{ minHeight: { xs: 56, sm: 64 }, px: { xs: 1.5, sm: 2 } }}>
          {isMobile && (
            <IconButton
              color="inherit"
              edge="start"
              onClick={() => setMobileOpen(true)}
              sx={{ mr: 0.5 }}
              aria-label="Open menu"
            >
              <MenuIcon />
            </IconButton>
          )}
          <Box sx={{ minWidth: 0, flex: 1 }}>
            <Typography fontWeight={900} noWrap sx={{ fontSize: { xs: '0.95rem', sm: '1.1rem' } }}>
              {NAV.find((n) => location.pathname.startsWith(n.to))?.label || 'Local Partners'}
            </Typography>
            <Typography variant="caption" sx={{ opacity: 0.9, display: 'block' }} noWrap>
              {vendor?.name || 'Shop'}
            </Typography>
          </Box>
          <Stack direction="row" spacing={0.75} alignItems="center" sx={{ ml: 1, flexShrink: 0 }}>
            {soldTotal != null && (
              <Chip
                component={RouterLink}
                to="/orders"
                clickable
                label={`Sold ${soldTotal.toFixed(0)} ${currency}`}
                sx={{
                  fontWeight: 800,
                  bgcolor: 'rgba(255,255,255,0.14)',
                  color: '#fff',
                  border: '1px solid rgba(255,255,255,0.28)',
                  '& .MuiChip-label': { px: 1, fontSize: { xs: '0.7rem', sm: '0.8rem' } },
                  maxWidth: { xs: 110, sm: 160 },
                }}
              />
            )}
            {walletBalance != null && (
              <Chip
                component={RouterLink}
                to="/transactions"
                clickable
                icon={<AccountBalanceWalletIcon sx={{ color: '#fff !important', fontSize: 18 }} />}
                label={`${walletBalance.toFixed(2)} ${currency}`}
                sx={{
                  fontWeight: 800,
                  bgcolor: 'rgba(255,255,255,0.18)',
                  color: '#fff',
                  border: '1px solid rgba(255,255,255,0.35)',
                  '& .MuiChip-label': { px: 1, fontSize: { xs: '0.7rem', sm: '0.8rem' } },
                  maxWidth: { xs: 120, sm: 180 },
                }}
              />
            )}
          </Stack>
        </Toolbar>
      </AppBar>

      <Box component="nav" sx={{ width: { md: DRAWER_WIDTH }, flexShrink: { md: 0 } }}>
        <Drawer
          variant="temporary"
          open={mobileOpen}
          onClose={() => setMobileOpen(false)}
          ModalProps={{ keepMounted: true }}
          sx={{
            display: { xs: 'block', md: 'none' },
            '& .MuiDrawer-paper': {
              width: 'min(85vw, 260px)',
              boxSizing: 'border-box',
              pt: 'env(safe-area-inset-top)',
              pb: 'env(safe-area-inset-bottom)',
            },
          }}
        >
          {drawer}
        </Drawer>
        <Drawer
          variant="permanent"
          open
          sx={{
            display: { xs: 'none', md: 'block' },
            '& .MuiDrawer-paper': {
              width: DRAWER_WIDTH,
              boxSizing: 'border-box',
              borderRight: '1px solid',
              borderColor: 'divider',
            },
          }}
        >
          {drawer}
        </Drawer>
      </Box>

      <Box
        component="main"
        sx={{
          flexGrow: 1,
          width: { md: `calc(100% - ${DRAWER_WIDTH}px)` },
          minWidth: 0,
          maxWidth: '100%',
        }}
      >
        <Toolbar sx={{ minHeight: { xs: 56, sm: 64 } }} />
        {showWalletBanner && walletBalance != null && (
          <Alert
            severity="error"
            variant="filled"
            sx={{
              borderRadius: 0,
              alignItems: 'center',
              py: 1,
              '& .MuiAlert-message': { width: '100%' },
            }}
            action={
              <Stack direction="row" spacing={1} sx={{ flexShrink: 0, pr: 1 }}>
                <Button
                  color="inherit"
                  size="small"
                  variant="outlined"
                  component={RouterLink}
                  to="/topup"
                  sx={{
                    borderColor: 'rgba(255,255,255,0.7)',
                    fontWeight: 800,
                    whiteSpace: 'nowrap',
                  }}
                >
                  Top up
                </Button>
                <Button
                  color="inherit"
                  size="small"
                  href={`${SUPPORT_WHATSAPP_URL}?text=${encodeURIComponent(
                    `Hi GoSnel Support — my Local Partner wallet balance is ${currency} ${Number(
                      walletBalance
                    ).toFixed(2)}. I need help topping up.`
                  )}`}
                  target="_blank"
                  rel="noopener noreferrer"
                  sx={{ fontWeight: 700, whiteSpace: 'nowrap' }}
                >
                  Contact support
                </Button>
              </Stack>
            }
          >
            <Typography variant="body2" fontWeight={800}>
              Wallet balance: {currency} {Number(walletBalance).toFixed(2)} — top
              up to keep Neighbour orders running. Banner clears after balance is
              above ~$10.
            </Typography>
          </Alert>
        )}
        <Box
          sx={{
            p: { xs: 1.5, sm: 2.5, md: 3 },
            // Extra bottom space so FABs (AI + WhatsApp) don't cover page CTAs
            pb: {
              xs: 'calc(9rem + env(safe-area-inset-bottom))',
              sm: 'calc(8.5rem + env(safe-area-inset-bottom))',
            },
            maxWidth: 1100,
            mx: 'auto',
            width: '100%',
          }}
        >
          {children}
        </Box>
      </Box>

      <LpAiReturnChip />
      <LpGosnelAiFab />
      <LpWhatsAppFab />
    </Box>
  );
}
